import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AccountInfo, AuthenticationResult, BrowserAuthError, Configuration, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { ACCESS_TOKEN_KEY, EXPIRESIN, LOGIN_FAIL, REFRESH_TOKEN_KEY } from '@constants/common.constants';
import { Environment } from '@environment/environment';

import { EnvironmentNames } from '@environment/environment.enum';
import { GenericError } from '@models/classes/generic.error';
import { MsalConfig, MsalConfigResponseDTO } from '@models/dto/msal-config-response.dto';
import { ErrorCode } from '@models/enums/error.codes';
import { AjaxResponse } from '@models/types/ajax-response.type';
import { RolesService } from '@modules/home/services/roles.service';
import { AuthService } from '@services/auth/auth.service';
import { HttpService } from '@services/http/http.service';
import { StorageService } from '@services/storage/storage.service';
import { BehaviorSubject, catchError, map, Subject } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class MsalAuthService {
    //readonly #loggerSvc: ErrorLogDBService = inject(ErrorLogDBService);
    private readonly authSvc: AuthService = inject(AuthService);
    private readonly router: Router = inject(Router);
    private seqData: any = null;
    msalInstance: PublicClientApplication;

    //readonly #loggerSvc: ErrorLogDBService = inject(ErrorLogDBService);

    public readonly roleSvc: RolesService = inject(RolesService);
    get isAuthenticated(): boolean {
        return !!this.activeAccount;
    }

    private readonly storageSvc: StorageService = inject(StorageService);
    get activeAccount(): AccountInfo | null {
        const accounts = this.msalInstance?.getAllAccounts();
        const activeAccount = accounts.length ? accounts[0] : null;
        return activeAccount;
    }

    get userName(): string | null {
        const activeAccount = this.msalInstance?.getActiveAccount();
        if (activeAccount) {
            return activeAccount.username.split('@')[0].toUpperCase();
        } else {
            return null;
        }
    }


    get activeUserName(): string | null {
        const secondaryUser = this.authSvc.getAuthData();
        if (secondaryUser) {
            return secondaryUser.userName;
        }
        return this.userName;
    }






    userFullName$ = new BehaviorSubject<string | undefined>('');

    getAccessToken(): string | null {
        const data = this.storageSvc.getSessionData(ACCESS_TOKEN_KEY);
        return data;
    }

    get isTokenExpired(): boolean {
        const value = this.storageSvc.getSessionData(EXPIRESIN);
        if (!value) {
            return true;
        }
        const expireDate = new Date(value);
        const currentDate = new Date();
        if (expireDate > currentDate) {
            return false;
        } else {
            return true;
        }
    }
    authChanged$: Subject<boolean> = new Subject<boolean>();

    http: HttpService = inject(HttpService);

    constructor() { }

    async init() {
        const envMsalConfig = await this.getMsalConfig();

        const msalConfig: Configuration = {
            auth: {
                clientId: envMsalConfig.clientId,
                authority: envMsalConfig.tenantId,
                redirectUri: envMsalConfig.redirectUrl,
                postLogoutRedirectUri: envMsalConfig.postLogoutRedirectUri,
            },
            cache: {
                cacheLocation: 'sessionStorage',
                temporaryCacheLocation: 'sessionStorage',
                secureCookies: true,
            },
            system: {
                loggerOptions: {
                    loggerCallback: (level: LogLevel, message: string, containsPii: boolean): void => {
                        if (containsPii) {
                            return;
                        }
                        switch (level) {
                            case LogLevel.Error:
                                if (Environment.environment == EnvironmentNames.local) {
                                    console.error(message);
                                } else {
                                    const msalError = 'error in msalAuthSvc > init() > loggerCallback() > error'; //new GenericError(message, ErrorCode.SomethingWentWrong, 'error in msalAuthSvc > init() > loggerCallback() > error');
                                    //this.#loggerSvc.add(msalError)
                                    console.log(msalError);
                                }
                                break;
                            case LogLevel.Info:
                                if (Environment.environment == EnvironmentNames.local) {
                                    console.info(message);
                                }
                                break;
                            case LogLevel.Verbose:
                                if (Environment.environment == EnvironmentNames.local) {
                                    console.debug(message);
                                }
                                break;
                            case LogLevel.Warning:
                                console.warn(message);
                                break;
                        }
                    },
                    piiLoggingEnabled: false,
                },
            },
            telemetry: {
                application: {
                    appName: 'Audit 2.0',
                    appVersion: '1.0.0',
                },
            },
        };

        const instance = new PublicClientApplication(msalConfig);
        await instance.initialize();
        instance
            .handleRedirectPromise()
            .then((response) => {
                if (response !== null) {
                    instance.setActiveAccount(response.account);
                    this.handleLoginSuccess(response.idToken);
                } else {
                    const idToken = instance.getActiveAccount()?.idToken
                    if (idToken) {
                        this.handleLoginSuccess(idToken);
                    }
                }
            })
            .catch((error) => {
                console.error(error);
            });

        return instance;
    }


    async getMsalConfig(): Promise<MsalConfig> {
        if (Environment.environment == EnvironmentNames.local) {
            const msalConfig: MsalConfig = {
                clientId: '7d659ef1-956d-43e0-b47c-1109c2cc6ea4',
                redirectUrl: 'http://localhost:4200/home',
                tenantId: 'https://login.microsoftonline.com/400696bb-3ef5-44ed-b838-ceb5afd17d90',
                postLogoutRedirectUri: 'http://localhost:4200/auth',
            };
            return msalConfig;
        }
        const apiUrl = `${Environment.baseUrl}public/auth/config`;
        const response = await this.http.get<AjaxResponse<MsalConfigResponseDTO>>([apiUrl], true);
        if (response.success) {
            const msalConfig: MsalConfig = {
                clientId: response.data.clientId,
                redirectUrl: response.data.redirectUrl,
                tenantId: 'https://login.microsoftonline.com/400696bb-3ef5-44ed-b838-ceb5afd17d90',
                postLogoutRedirectUri: `${response.data.redirectUrl}/auth`
            };
            return msalConfig;
        } else {
            throw new GenericError({
                errorCode: ErrorCode.SomethingWentWrong,
                message: 'Failed to fetch MSAL configuration',
                date: new Date(),
                location: 'msal-auth.service.ts > getMsalConfig()'
            });
        }

    }

    // Add method to handle redirect response
    handleRedirectPromise(): Promise<AuthenticationResult | null> {
        return this.msalInstance.handleRedirectPromise();
    }

    async Login(): Promise<AuthenticationResult | void> {
        try {
            await this.msalInstance.loginRedirect({
                scopes: ['User.Read', 'openid', 'profile', 'email', 'offline_access'],
            });

            //return result;
        } catch (error) {
            if (error instanceof BrowserAuthError && error.errorCode === 'user_cancelled') {
                console.warn('User cancelled the login process.');
                // Optionally, provide a user prompt or retry mechanism here
            } else {
                console.error('Login failed due to an unexpected error:', error);
            }
            throw error; // Re-throw the error if you want higher-level handling
        }
    }

    private async handleLoginSuccess(idToken: string): Promise<void> {
        if (idToken) {
            // Exchange idToken for accessToken and refreshToken
            try {
                const tokenResponse = await this.exchangeIdTokenForTokens(idToken);
                if (tokenResponse) {
                    this.authSvc.setAuthData(tokenResponse?.data);
                    setTimeout(() => {

                        this.authChanged$.next(true);
                    }, 1500);
                }
            } catch (error) {
                this.authChanged$.next(false);
                sessionStorage.clear();
                this.storageSvc.setSessionData(LOGIN_FAIL, 'Login Fail');
                await this.router.navigate(['/auth']);
            }
        } else {
            this.authChanged$.next(false);
            const err = new GenericError();
            err.message = 'ID token missing in authentication result.';

            throw err;
        }
    }

    private async exchangeIdTokenForTokens(idToken: string): Promise<any> {
        if (!idToken) {
            console.error('ID token is null or undefined');
            throw new Error('ID token is required');
        }

        const body = { idToken };
        const url = `${Environment.idtokenValidate}/gettoken`;

        try {
            // Use firstValueFrom for better async/await handling
            const response = await this.http.post<any>([url, body], true);
            this.setAuthData(response.data);
            this.setEmpSeq(response.data);
            this.storageSvc.removeSession(LOGIN_FAIL);
            return response;
        } catch (error) {
            throw error;
        }
    }

    getEmpSeq() {
        return this.seqData;
    }

    private setEmpSeq(data: any) {
        this.seqData = data;
    }

    setAuthData(tokenResponse: any): void {
        this.storageSvc.setSessionData(ACCESS_TOKEN_KEY, tokenResponse.accessToken);
        this.storageSvc.setSessionData(REFRESH_TOKEN_KEY, tokenResponse.refreshToken);
        if (tokenResponse.expiresOn) {
            this.storageSvc.setSessionData(EXPIRESIN, tokenResponse.expiresOn);
        }
    }

    refreshToken() {
        return this.http
            .post<any>([
                `${Environment.idRefreshTokenValidate}/refresh-token`,
                {
                    refreshToken: this.storageSvc.getSessionData(REFRESH_TOKEN_KEY),
                },
            ])
            .pipe(
                map((user) => {
                    if (user?.data && user?.data?.accessToken) {
                        this.setAuthData(user?.data);
                    }
                    return user;
                }),
                catchError((error) => {
                    throw error;
                })
            );
    }

    logoutRedirect(): Promise<void> {
        if (!this.msalInstance) {
            throw new Error('MSAL instance is not initialized');
        }
        return this.msalInstance.logoutRedirect();
    }
}
